import { format, parse } from "date-fns"
import { ScheduledPostType } from "../../types"
import verifiedIcon from '@/assets/verified.svg'
import { Compass, X } from "lucide-react"
import { Card } from "@/shared/ui/card.tsx"
import { cn } from "@/lib/utils"
import EditPostSheet from "@/shared/edit-post-with-gallery/EditPostSheet"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/ui/alert-dialog.tsx"
import { useDiscardPost } from "../../api/socialpost-api"
import { useAtomValue } from "jotai"
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms"
import ImageCarousel from "./ImageCarousel"
import ImageDialog from "./ImageDialog"

type ScheduledPostCardProps = {
  data: ScheduledPostType,
  date: string
}

const ScheduledPostCard = ({ data, date }: ScheduledPostCardProps) => {

  const { discardPost } = useDiscardPost()

  const theme = useAtomValue(themeAtom)

  return (
    <Card className="w-full break-inside-avoid group relative mt-1 mb-3 lg:mb-8 p-5 pb-1 flex flex-col justify-around gap-2 rounded-2xl">
      <img src={`https://cdn.simpleicons.org/${(data.platform.toLowerCase().includes("twitter")) ? "x" : data.platform.toLowerCase()}`} className="absolute w-7 top-3 right-3" />
      <div className="flex items-center gap-2">
        <img src={theme.avatar} className="w-8 aspect-square rounded-full" />
        <div className="flex flex-col">
          <p className="text-sm flex items-center gap-1 font-semibold">{theme.company}
            <img src={verifiedIcon} alt="" className="w-4" />
          </p>
          <p className="text-xs text-gray-600">@{theme.handle}</p>
        </div>
      </div>
      <p className="text-xs lg:text-sm !leading-6 tracking-wide">{data.post_content}</p>
      <p className="font-semibold text-sm text-gray-500">
        Scheduled on {format(parse(data.post_time, "HH:mm:ss", new Date()), "hh:mm a")} on {data.platform}
      </p>
      {data.image_url !== null && data.image_id !== null && data.image_url.length !== 0 && (
        <ImageDialog image_video_recommendation={data.image_video_recommendation} currentImg={data.image_url} image_id={data.image_id} />
      )}
      {data.image_ids !== null && (
      <ImageCarousel images={data.image_ids} />
      )}
      <div className="pt-2 pb-2 border-t flex w-full justify-between items-center gap-1 pe-2">
        <p className={cn("text-[0.6rem] 2xl:text-xs px-2 py-1 w-fit max-w-[90%] text-gray-600 shadow-md rounded-full border border-gray-100",
          data.post_type === "Campaign" ? "bg-gradient-to-r from-campaign-light to-campaign-light/30"
            : "bg-gradient-to-r from-post-light to-post-light/30")}>{data.post_type} for {data.event_name}
        </p>
        {/* <EditPostSheet
          event_status="Scheduled"
          event_id={data.event_id}
          post_id={data.post_id}
          content={data.post_content}
          event_name={data.event_name}
          img_recommendation={data.image_video_recommendation}
          post_date={date}
          tags={data.tags}
          trigger={<div className="flex gap-1 hover:bg-neutral-200 rounded-xl cursor-pointer py-1 px-2 items-center text-gray-500">
            <Compass size={13} />
            <span className="text-[0.6rem] lg:text-xs">Edit</span>
          </div>}
        /> */}
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <div className="flex gap-1 hover:bg-neutral-200 rounded-xl cursor-pointer py-1 px-2 items-center text-gray-500">
                <X size={13} />
                <span className="text-[0.6rem] lg:text-xs">Discard</span>
              </div>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete this post
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                  discardPost(data.post_id)
                }}>Continue</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <EditPostSheet 
            location="scheduled"
            variant="scheduled"
            onSchedule={(id: number) => {console.log(id, " post scheduled")}}
            postDetails={{ ...data, post_date: date }} 
            eventDetails={{ event_id: data.event_id, event_name: data.event_name, story_id: data.story_id }}
            trigger={<div className="flex gap-1 hover:bg-neutral-200 rounded-xl cursor-pointer py-1 px-2 items-center text-gray-500">
              <Compass size={13} />
              <span className="text-[0.6rem] lg:text-xs">Edit</span>
            </div>}
          />
        </div>
      </div>
    </Card>
  )
}

export default ScheduledPostCard
